import React, { useState, useMemo, useEffect } from 'react';
import { APICONSTANTS, CONSTANTS, TABLE_CONSTANTS } from '../Constants';
import UsersTableProgressCol from '../proband-info/UsersProgressStatus';
import Table from '../UI/table/Table';
import { FormControl, Grid, MenuItem, Select } from '@mui/material';
import axios from 'src/axios';
import { useNavigate } from 'react-router-dom';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export default function ProgressTrackerTable({
  schemas,
  users,
  selectedWorkflow,
  setSelectedWorkflow
}) {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('forms');
  const navigate = useNavigate();
  const viewForm = (id) =>
    id &&
    navigate(CONSTANTS.formResponsesPath.replace(':_id', id), {
      state: {
        from: CONSTANTS.formProgressTracker
      }
    });

  useEffect(() => {
    axios
      .get(APICONSTANTS.formBuilderWorkflowSchemas)
      .then(({ data }) => setWorkflows(data.workflows));
  }, []);

  const columns = useMemo(() => {
    const defaultValues = [
      {
        accessorKey: 'email',
        header: 'E-mail',
        size: 100
      }
    ];

    let newValues;

    if (typeof selectedWorkflow === 'string') {
      newValues = schemas.map((schema) => ({
        id: schema._id, // Give unique id to avoid warning
        header: <div style={{ paddingLeft: 10 }}>{schema.title}</div>,
        accessorFn: (row) => row.form[schema._id]?.status,
        Cell: ({ row }) => (
          <div onClick={() => viewForm(row.original.form[schema._id]?.resId)}>
            <UsersTableProgressCol
              status={row.original.form[schema._id]?.status}
            />
          </div>
        ),
        size: 100,
        muiTableHeadCellProps: {
          sx: { textAlign: 'center' }
        },
        muiTableBodyCellProps: {
          sx: { textAlign: 'center' }
        }
      }));
    } else {
      const workflowItems = [];

      selectedWorkflow.forms.forEach((id) => {
        schemas.forEach((schema) => {
          if (schema._id === id) {
            workflowItems.push({
              id: schema._id,
              header: <div style={{ paddingLeft: 10 }}>{schema.title}</div>,
              accessorFn: (row) => row.form[schema._id]?.status,
              Cell: ({ row }) => (
                <div
                  onClick={() => viewForm(row.original.form[schema._id]?.resId)}
                >
                  <UsersTableProgressCol
                    status={row.original.form[schema._id]?.status}
                  />
                </div>
              ),
              size: 100,
              muiTableHeadCellProps: {
                sx: { textAlign: 'center' }
              },
              muiTableBodyCellProps: {
                sx: { textAlign: 'center' }
              }
            });
          }
        });
      });

      newValues = workflowItems;
    }

    return [...defaultValues, ...newValues];
  }, [schemas, selectedWorkflow]);

  const table = useMemo(() => {
    const pageSize = users.length > 10 ? 20 : 10;

    return (
      <div style={{ position: 'relative' }}>
        <Table
          title={'Pathway Progress Tracker'}
          rows={users}
          columns={columns}
          pageSize={5}
          pageSizeOptions={[5, 10, 15, 20, 30, 40, 50]}
          dense={true}
          tableKey={TABLE_CONSTANTS.progressTracker}
        />
        <div style={{ position: 'absolute', bottom: 10 }}>
          <ChartHelper />
        </div>
        <div style={{ position: 'absolute', top: 10, left: 310 }}>
          <DropDown
            workflows={workflows}
            setSelectedWorkflow={setSelectedWorkflow}
            selectedWorkflowId={selectedWorkflowId}
            setSelectedWorkflowId={setSelectedWorkflowId}
          />
        </div>
      </div>
    );
  }, [columns, users, selectedWorkflowId, workflows]);

  return (
    <Grid container>
      <Grid size={12}>{table}</Grid>
    </Grid>
  );
}

const ChartHelper = () => {
  const userStatus = CONSTANTS.user_status;

  const t = useAutoTranslation();

  const STATUS = {
    [userStatus.DEACTIVATED]: 'Deactivated',
    [userStatus.CREATED]: 'Created',
    [userStatus.PENDING]: 'Pending',
    [userStatus.IN_PROGRESS]: 'In Progress',
    [userStatus.SUBMITTED]: 'Submitted',
    [userStatus.IMPORTED]: 'Imported'
  };

  return (
    <div style={{ display: 'flex', marginInline: 10 }}>
      {Object.keys(userStatus).map((status) => {
        return (
          <span
            key={status}
            style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}
          >
            <UsersTableProgressCol status={status} />
            <span style={{ marginInline: 5 }}>{t(STATUS[status])}</span>
          </span>
        );
      })}
    </div>
  );
};

const DropDown = ({
  workflows,
  setSelectedWorkflow,
  selectedWorkflowId,
  setSelectedWorkflowId
}) => {
  const handleChange = (e) => {
    setSelectedWorkflowId(e.target.value);
    const workflow = workflows.find(
      (workflow) => workflow._id === e.target.value
    );
    workflow
      ? setSelectedWorkflow({
          forms: workflow.selectedForms,
          id: workflow._id,
          name: workflow.workflowName
        })
      : setSelectedWorkflow('forms');
  };

  return (
    <FormControl sx={{ m: 0.2, minWidth: 170 }} size="small">
      <Select
        lable="select"
        value={selectedWorkflowId}
        onChange={handleChange}
        autoWidth
        variant="outlined"
      >
        <MenuItem value="forms">Select Pathway</MenuItem>
        {workflows.map((workflow) => {
          return (
            <MenuItem key={workflow._id} value={workflow._id}>
              {workflow.workflowName}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
